import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import Heatmap from '../../../widgets/Heatmap';
import Progress from '../../../widgets/Progress';
import { examsSelectors } from '../../../slices/exams/examsSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { Check, Plus } from 'react-feather';
import CustomModal from '../../Global/CustomModal';
import TopicRadar from '../../../widgets/TopicRadar';
import QBankPerformance from '../../../widgets/QBankPerformance';
import FlashcardTrend from '../../../widgets/FlashcardTrend';
import MyBookmarks from '../../../widgets/MyBookmarks';
import DaysUntilExam from '../../../widgets/DaysUntilExam';
import { WIDGET_KEYS } from '../../../types/Widgets';
import { hapticsImpactLight } from '../../../utils/haptics';
import { updateCurrentUserInfo } from '../../../services/auth';

const EditableLayout: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAddWidgetModalOpen, setIsAddWidgetModalOpen] = useState(false);
  const [isDefaultLayout, setIsDefaultLayout] = useState(true);
  const [customLayout, setCustomLayout] = useState<WIDGET_KEYS[]>(
    [
      WIDGET_KEYS.PROGRESS,
      moment(currentUser?.createdAt).diff(moment(), 'days') < 0 &&
      primaryExam?.id
        ? WIDGET_KEYS.HEATMAP
        : null,
    ].filter((widget): widget is WIDGET_KEYS => widget !== null)
  );
  const [modifiedLayout, setModifiedLayout] = useState<WIDGET_KEYS[]>([]);

  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const isDefault =
      (customLayout.length === 2 &&
        customLayout.includes(WIDGET_KEYS.PROGRESS) &&
        customLayout.includes(WIDGET_KEYS.HEATMAP)) ||
      (customLayout.length === 1 &&
        customLayout.includes(WIDGET_KEYS.PROGRESS));
    setIsDefaultLayout(isDefault);
  }, [customLayout, primaryExam]);

  useEffect(() => {
    if (currentUser?.homeLayout) {
      setCustomLayout(currentUser.homeLayout);
    }
  }, [currentUser]);

  useEffect(() => {
    setModifiedLayout(customLayout);
  }, [customLayout]);

  useEffect(() => {
    if (
      modifiedLayout.length > 0 &&
      JSON.stringify(modifiedLayout) !== JSON.stringify(customLayout)
    ) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [modifiedLayout, customLayout]);

  const renderCustomLayout = () => {
    return customLayout.map((widgetKey, index) => {
      switch (widgetKey) {
        case WIDGET_KEYS.PROGRESS:
          return <Progress key={`${widgetKey}-${index}`} showDelete={true} />;
        case WIDGET_KEYS.HEATMAP:
          return <Heatmap key={`${widgetKey}-${index}`} showDelete={true} />;
        case WIDGET_KEYS.QBANK_PERFORMANCE:
          return (
            <QBankPerformance key={`${widgetKey}-${index}`} showDelete={true} />
          );
        case WIDGET_KEYS.TOPIC_RADAR:
          return <TopicRadar key={`${widgetKey}-${index}`} showDelete={true} />;
        case WIDGET_KEYS.FLASHCARD_TREND:
          return (
            <FlashcardTrend key={`${widgetKey}-${index}`} showDelete={true} />
          );
        case WIDGET_KEYS.DAYS_UNTIL_EXAM:
          return (
            <DaysUntilExam key={`${widgetKey}-${index}`} showDelete={true} />
          );
        case WIDGET_KEYS.MY_BOOKMARKS:
          return (
            <MyBookmarks key={`${widgetKey}-${index}`} showDelete={true} />
          );
      }
    });
  };

  const handleAddOrRemoveWidget = async (widgetKey: WIDGET_KEYS) => {
    if (modifiedLayout.includes(widgetKey)) {
      setModifiedLayout(modifiedLayout.filter((key) => key !== widgetKey));
    } else {
      setModifiedLayout([...modifiedLayout, widgetKey]);
    }
  };

  const handleSaveLayout = async () => {
    const updateUser = await updateCurrentUserInfo({
      homeLayout: modifiedLayout,
    });
    if (updateUser && updateUser.homeLayout) {
      setCustomLayout(updateUser.homeLayout);
    }
    setIsAddWidgetModalOpen(false);
  };

  const handleUnsavedChanges = () => {
    if (cancelButtonRef.current) {
      cancelButtonRef.current.classList.add('shake');
    }
    if (submitButtonRef.current) {
      submitButtonRef.current.classList.add('shake');
    }
    setTimeout(() => {
      if (cancelButtonRef.current) {
        cancelButtonRef.current.classList.remove('shake');
      }
      if (submitButtonRef.current) {
        submitButtonRef.current.classList.remove('shake');
      }
    }, 500);
  };

  return (
    <>
      <div
        className={`editable-layout ${isDefaultLayout ? 'editable-layout--default' : ''} ${customLayout.length === 0 ? 'editable-layout--empty' : ''}`}
        style={{
          gridTemplateColumns:
            customLayout.length > 2 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
        }}
      >
        {renderCustomLayout()}
        <button
          className="button button--add-widget"
          onClick={() => setIsAddWidgetModalOpen(true)}
          tabIndex={0}
          aria-label="Add Widget"
          role="button"
        >
          <div className="button--add-widget_button button--float">
            <Plus />
          </div>
          <span>Add Widgets</span>
        </button>
      </div>
      <CustomModal
        open={isAddWidgetModalOpen}
        onClose={() => setIsAddWidgetModalOpen(false)}
        name="add-widget"
        glass={true}
        unsavedChanges={hasUnsavedChanges}
        handleUnsavedChanges={handleUnsavedChanges}
      >
        <div className="modal_header">
          <h2>Select Widgets</h2>
        </div>
        <div className="modal_content">
          <div className="widget-grid">
            <div
              className={`widget-grid_item ${
                modifiedLayout.includes(WIDGET_KEYS.PROGRESS)
                  ? 'widget-grid_item--active'
                  : ''
              }`}
              onClick={() => {
                handleAddOrRemoveWidget(WIDGET_KEYS.PROGRESS);
                hapticsImpactLight();
              }}
              tabIndex={0}
              aria-label="Add Progress Widget"
              role="button"
            >
              <div className="widget-grid_item_icon">
                <Check />
              </div>
              <Progress isDemo={true} />
            </div>
            <div
              className={`widget-grid_item ${
                modifiedLayout.includes(WIDGET_KEYS.HEATMAP)
                  ? 'widget-grid_item--active'
                  : ''
              }`}
              onClick={() => {
                handleAddOrRemoveWidget(WIDGET_KEYS.HEATMAP);
                hapticsImpactLight();
              }}
              tabIndex={0}
              aria-label="Add Heatmap Widget"
              role="button"
            >
              <div className="widget-grid_item_icon">
                <Check />
              </div>
              <Heatmap isDemo={true} />
            </div>
            <div
              className={`widget-grid_item ${
                modifiedLayout.includes(WIDGET_KEYS.QBANK_PERFORMANCE)
                  ? 'widget-grid_item--active'
                  : ''
              }`}
              onClick={() => {
                handleAddOrRemoveWidget(WIDGET_KEYS.QBANK_PERFORMANCE);
                hapticsImpactLight();
              }}
              tabIndex={0}
              aria-label="Add QBank Performance Widget"
              role="button"
            >
              <div className="widget-grid_item_icon">
                <Check />
              </div>
              <QBankPerformance isDemo={true} />
            </div>
            <div
              className={`widget-grid_item ${
                modifiedLayout.includes(WIDGET_KEYS.TOPIC_RADAR)
                  ? 'widget-grid_item--active'
                  : ''
              }`}
              onClick={() => {
                handleAddOrRemoveWidget(WIDGET_KEYS.TOPIC_RADAR);
                hapticsImpactLight();
              }}
              tabIndex={0}
              aria-label="Add Topic Radar Widget"
              role="button"
            >
              <div className="widget-grid_item_icon">
                <Check />
              </div>
              <TopicRadar isDemo={true} />
            </div>
            <div
              className={`widget-grid_item ${
                modifiedLayout.includes(WIDGET_KEYS.FLASHCARD_TREND)
                  ? 'widget-grid_item--active'
                  : ''
              }`}
              onClick={() => {
                handleAddOrRemoveWidget(WIDGET_KEYS.FLASHCARD_TREND);
                hapticsImpactLight();
              }}
              tabIndex={0}
              aria-label="Add Flashcard Trend Widget"
              role="button"
            >
              <div className="widget-grid_item_icon">
                <Check />
              </div>
              <FlashcardTrend isDemo={true} />
            </div>
            <div
              className={`widget-grid_item ${
                modifiedLayout.includes(WIDGET_KEYS.MY_BOOKMARKS)
                  ? 'widget-grid_item--active'
                  : ''
              }`}
              onClick={() => {
                handleAddOrRemoveWidget(WIDGET_KEYS.MY_BOOKMARKS);
                hapticsImpactLight();
              }}
              tabIndex={0}
              aria-label="Add My Bookmarks Widget"
              role="button"
            >
              <div className="widget-grid_item_icon">
                <Check />
              </div>
              <MyBookmarks isDemo={true} />
            </div>
            <div
              className={`widget-grid_item ${
                modifiedLayout.includes(WIDGET_KEYS.DAYS_UNTIL_EXAM)
                  ? 'widget-grid_item--active'
                  : ''
              }`}
              onClick={() => {
                handleAddOrRemoveWidget(WIDGET_KEYS.DAYS_UNTIL_EXAM);
                hapticsImpactLight();
              }}
              tabIndex={0}
              aria-label="Add Days Until Exam Widget"
              role="button"
            >
              <div className="widget-grid_item_icon">
                <Check />
              </div>
              <DaysUntilExam isDemo={true} />
            </div>
          </div>
        </div>
        <div className="modal_actions">
          <button
            className="button button--secondary"
            onClick={() => {
              setModifiedLayout(customLayout);
              setIsAddWidgetModalOpen(false);
              hapticsImpactLight();
            }}
            ref={cancelButtonRef}
            tabIndex={0}
            aria-label="Close Widgets"
          >
            Close
          </button>
          <button
            className="button"
            onClick={handleSaveLayout}
            ref={submitButtonRef}
            disabled={!hasUnsavedChanges}
            tabIndex={0}
            aria-label="Save Widgets"
          >
            Save Widgets
          </button>
        </div>
      </CustomModal>
    </>
  );
};

export default EditableLayout;
